import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Tag, Cube, CheckCircle, Truck, ShoppingCart, Minus, Plus, BookmarkSimple } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Reveal";
import { OrderModal } from "@/components/site/OrderModal";
import { getSupplyProduct, getSavedLists, addToSavedList, createSavedList } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { openCartDrawer } from "@/lib/cart";
import { getToken } from "@/lib/auth";
import { toast } from "sonner";

const stockStyle = (s) =>
  s === "In stock" ? "bg-green-100 text-green-700" : s === "Backorder" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600";

const isLowStock = (p) =>
  p.stock_qty != null && p.stock_qty > 0 && p.stock_qty <= (p.low_stock_threshold || 5) && (p.stock_status === "In stock" || p.enforce_stock);
const stockLabel = (p) =>
  (p.enforce_stock && p.stock_qty === 0) ? "Out of stock" :
  isLowStock(p) ? `Only ${p.stock_qty} left` :
  (p.stock_status === "In stock" && p.stock_qty === 0) ? "Out of stock" : p.stock_status;
const stockLabelStyle = (p) =>
  isLowStock(p) ? "bg-orange-100 text-orange-700" : ((p.enforce_stock && p.stock_qty === 0) || (p.stock_status === "In stock" && p.stock_qty === 0)) ? "bg-slate-200 text-slate-600" : stockStyle(p.stock_status);

export default function SupplyProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [ordering, setOrdering] = useState(false);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const [saveOpen, setSaveOpen] = useState(false);
  const [lists, setLists] = useState([]);
  const token = getToken();

  const openSave = async () => {
    if (!token) { toast.error("Sign in to save products to a list."); return; }
    try { setLists(await getSavedLists(token)); setSaveOpen((v) => !v); }
    catch { toast.error("Could not load your lists."); }
  };
  const saveTo = async (listId) => {
    try { await addToSavedList(token, listId, id); toast.success("Saved to list."); setSaveOpen(false); }
    catch { toast.error("Could not save."); }
  };
  const saveToNew = async () => {
    try { const l = await createSavedList(token, "My list"); await addToSavedList(token, l.id, id); toast.success("Saved to new list."); setSaveOpen(false); }
    catch { toast.error("Could not save."); }
  };

  useEffect(() => {
    getSupplyProduct(id).then((d) => { setP(d); setActiveImg(0); }).catch(() => setP(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center pt-32"><p className="text-slate-500" data-testid="supply-product-loading">Loading…</p></div>;

  if (!p) return (
    <div className="min-h-[60vh] grid place-items-center pt-32 px-5 text-center" data-testid="supply-product-not-found">
      <div>
        <h1 className="font-display font-black text-3xl text-navy-900">Product not found.</h1>
        <button onClick={() => navigate("/supply")} className="inline-flex items-center gap-2 mt-6 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600"><ArrowLeft size={16} /> Back to catalog</button>
      </div>
    </div>
  );

  const priceLabel = p.call_for_pricing || p.price == null ? "Call for pricing" : `$${Number(p.price).toFixed(2)}`;
  const specLines = (p.specs || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const purchasable = !p.call_for_pricing && p.price != null;
  const moq = Math.max(1, p.min_order_qty || 1);
  // Cap ordering to stock on hand only when the admin enabled the limit for this item
  const capped = p.enforce_stock && p.stock_qty != null;
  const maxQty = capped ? Number(p.stock_qty) : Infinity;
  const soldOut = capped && Number(p.stock_qty) <= 0;
  const orderQty = Math.min(Math.max(qty, moq), Number.isFinite(maxQty) ? Math.max(moq, maxQty) : qty);

  const handleAdd = () => {
    addItem(p, orderQty);
    toast.success(`Added ${orderQty} × ${p.name} to cart.`);
    openCartDrawer();
  };

  return (
    <div className="pt-28 md:pt-32" data-testid="supply-product-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-8">
        <Link to="/supply" data-testid="supply-back-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 mb-6"><ArrowLeft size={16} /> Back to catalog</Link>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <Reveal>
            <div>
              <div className="aspect-square bg-slate-50 rounded-3xl overflow-hidden grid place-items-center border border-slate-200">
                {p.images?.[activeImg] ? (
                  <img src={p.images[activeImg]} alt={p.name} className="w-full h-full object-contain p-8" />
                ) : (
                  <Package size={72} weight="thin" className="text-slate-300" />
                )}
              </div>
              {p.images?.length > 1 && (
                <div className="flex gap-3 mt-4">
                  {p.images.map((img, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 bg-slate-50 ${activeImg === i ? "border-navy-900" : "border-slate-200"}`}>
                      <img src={img} alt="" className="w-full h-full object-contain p-1.5" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.1}>
            <div>
              {p.brand && <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-500">{p.brand}</span>}
              <h1 className="font-display font-black text-3xl md:text-4xl text-navy-900 tracking-tight mt-2 leading-[1.05]">{p.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${stockLabelStyle(p)}`} data-testid="detail-stock-badge">{stockLabel(p)}</span>
                {p.sku && <span className="inline-flex items-center gap-1.5 text-sm text-slate-500"><Tag size={15} /> SKU: {p.sku}</span>}
                {p.category && <span className="inline-flex items-center gap-1.5 text-sm text-slate-500"><Cube size={15} /> {p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</span>}
              </div>

              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display font-black text-4xl text-navy-900">{priceLabel}</span>
                {!p.call_for_pricing && p.price != null && p.unit && <span className="text-slate-500 font-semibold">/ {p.unit}</span>}
              </div>
              {p.min_order_qty > 1 && <p className="text-sm text-slate-500 mt-1">Minimum order: {p.min_order_qty} {p.unit || "units"}</p>}

              {/* Tiered pricing */}
              {p.tiered_pricing?.length > 0 && (
                <div className="mt-5 rounded-2xl border border-slate-200 overflow-hidden" data-testid="supply-tiers">
                  <div className="bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-navy-500">Volume pricing</div>
                  <table className="w-full text-sm">
                    <tbody>
                      {p.tiered_pricing.map((t, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-4 py-2 text-slate-600">{t.min_qty}+ {p.unit || "units"}</td>
                          <td className="px-4 py-2 text-right font-bold text-navy-900">${Number(t.price).toFixed(2)} / {p.unit || "ea"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {p.description && <p className="text-slate-600 mt-6 leading-relaxed whitespace-pre-wrap">{p.description}</p>}

              {specLines.length > 0 && (
                <div className="mt-6" data-testid="supply-specs">
                  <h3 className="font-display font-black text-lg text-navy-900">Specifications</h3>
                  <ul className="mt-2 space-y-1.5">
                    {specLines.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle size={17} weight="fill" className="text-navy-500 shrink-0 mt-0.5" /> {s}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-8">
                {purchasable ? (
                  soldOut ? (
                    <div data-testid="supply-sold-out" className="inline-flex items-center gap-2 rounded-full bg-slate-200 text-slate-600 px-7 py-4 font-bold">
                      <Package size={18} weight="fill" /> Out of stock
                    </div>
                  ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="inline-flex items-center rounded-full border border-slate-300 overflow-hidden">
                      <button onClick={() => setQty(Math.max(moq, orderQty - 1))} aria-label="Decrease" data-testid="supply-qty-minus" className="w-11 h-12 grid place-items-center text-navy-900 hover:bg-slate-100"><Minus size={16} weight="bold" /></button>
                      <input data-testid="supply-qty" value={orderQty} onChange={(e) => setQty(Math.min(Number.isFinite(maxQty) ? maxQty : Infinity, Math.max(moq, parseInt(e.target.value) || moq)))} className="w-14 h-12 text-center font-bold text-navy-900 focus:outline-none" />
                      <button onClick={() => setQty(Math.min(Number.isFinite(maxQty) ? maxQty : Infinity, orderQty + 1))} disabled={Number.isFinite(maxQty) && orderQty >= maxQty} aria-label="Increase" data-testid="supply-qty-plus" className="w-11 h-12 grid place-items-center text-navy-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"><Plus size={16} weight="bold" /></button>
                    </div>
                    <button
                      onClick={handleAdd}
                      data-testid="supply-add-to-cart"
                      className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-7 py-4 font-bold hover:bg-navy-600 transition-colors"
                    >
                      <ShoppingCart size={18} weight="fill" /> Add to cart
                    </button>
                    <div className="relative">
                      <button onClick={openSave} data-testid="supply-save-btn" aria-label="Save to list" className="w-[52px] h-[52px] grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100 transition-colors">
                        <BookmarkSimple size={20} weight="regular" />
                      </button>
                      {saveOpen && (
                        <div data-testid="save-menu" className="absolute z-20 mt-2 right-0 w-60 bg-white border border-slate-200 rounded-xl shadow-xl p-2">
                          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 px-2 py-1">Save to list</p>
                          {lists.map((l) => (
                            <button key={l.id} onClick={() => saveTo(l.id)} data-testid={`save-to-${l.id}`} className="w-full text-left px-3 py-2 rounded-lg text-sm text-navy-900 hover:bg-slate-100">{l.name}</button>
                          ))}
                          <button onClick={saveToNew} data-testid="save-to-new" className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-navy-600 hover:bg-slate-100">+ New list</button>
                        </div>
                      )}
                    </div>
                    {capped && Number.isFinite(maxQty) && (
                      <p data-testid="supply-max-note" className="w-full text-xs font-semibold text-orange-600">Only {maxQty} in stock — you can't order more than that right now.</p>
                    )}
                  </div>
                  )
                ) : (
                  <>
                    <button
                      onClick={() => setOrdering(true)}
                      data-testid="supply-request-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-7 py-4 font-bold hover:bg-navy-600 transition-colors"
                    >
                      Request a quote
                    </button>
                    <p className="flex items-center gap-2 text-xs text-slate-500 mt-3"><Truck size={15} /> This item is call-for-pricing — submit a request and we'll confirm availability & price.</p>
                  </>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {ordering && (
        <OrderModal
          product={{ name: p.name, price: priceLabel, category: "AMWAP Supply" }}
          onClose={() => setOrdering(false)}
        />
      )}
    </div>
  );
}
